import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject, throwError } from 'rxjs';
import {
    catchError,
    delay,
    filter,
    map,
    take,
    tap,
    timeout,
} from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';

const TTL_LONG = 900000; /* 15 mins */
/** const TTL_SHORT = 5000; */ /* 5 secs */
const TIMEOUT = 60000;

/** introduced and only intended for use with coaching */
@Injectable()
export class CoachingCacheHttpInterceptor implements HttpInterceptor {
    private readonly _urlsRegExp: Array<{ ttl: number; url: RegExp }>;
    private readonly _cache: Map<
        string /* url */,
        {
            pending: boolean;
            expires: number;
            /* Date.now() */ resp: HttpResponse<any>;
        }
    >;

    private readonly _subj: Subject<HttpResponse<any>>;

    constructor(
        private readonly _translateService: TranslateService,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {
        // yes this could be changed into a multi-provider di
        this._urlsRegExp = [
            {
                ttl: TTL_LONG,
                url: new RegExp(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/coaching/lookupbox`,
                    'i',
                ),
            },
            {
                ttl: TTL_LONG,
                url: new RegExp(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/observations/config`,
                    'i',
                ),
            },
            {
                ttl: TTL_LONG,
                url: new RegExp(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/commitments/config`,
                    'i',
                ),
            },
            {
                ttl: TTL_LONG,
                url: new RegExp(
                    `${this._appConfig.API_ENDPOINTS.INSIGHT}/performance-data/data`,
                    'i',
                ),
            },
        ];

        this._cache = new Map();
        this._subj = new Subject();
    }

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<HttpEvent<any>> {
        let url = req.urlWithParams.toLowerCase();
        const match: { ttl: number } = this._urlsRegExp.find((e) =>
            e.url.test(url),
        );
        if (!match) {
            return next.handle(req);
        }

        url = `${url}${this._translateService.currentLang}`;
        if (req.body) {
            url = `${url}${JSON.stringify(req.body)}`;
        }
        const found = this._cache.has(url);
        const dat = found ? this._cache.get(url) : null;
        const hit = dat && !dat.pending && dat.expires > Date.now();
        if (!hit) {
            // TODO empty cache on user change
            this._cache.set(url, { pending: true, expires: null, resp: null });
            return next.handle(req).pipe(
                tap((ret) => {
                    if (ret instanceof HttpResponse && ret.status === 200) {
                        this._cache.set(url, {
                            pending: false,
                            expires: Date.now() + match.ttl,
                            resp: ret.clone(),
                        });
                        this._subj.next(ret);
                    }
                }),
            );
        }

        if (dat.pending) {
            return this._subj.asObservable().pipe(
                filter((resp) => resp.url === req.url),
                timeout(TIMEOUT),
                map((resp) => resp.clone()),
                take(1),
                delay(0), // force emulated asynchronicity
                catchError(() =>
                    throwError(new HttpErrorResponse({ status: 500 })),
                ),
            );
        }

        return of(dat.resp.clone()).pipe(
            delay(0), // force emulated asynchronicity
        );
    }
}
